import React, { useContext, useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { AuthContext } from '../AuthContext';

const API_BASE = 'https://api.insightos.co.uk';

const parseGalleryResponse = async (response) => {
  const text = await response.text();

  if (!text) {
    if (!response.ok) {
      throw new Error(`Request failed (${response.status})`);
    }
    return { success: true, data: [] };
  }

  let payload;
  try {
    payload = JSON.parse(text);
  } catch {
    throw new Error('Gallery endpoint returned invalid JSON.');
  }

  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.message || 'Failed to load gallery.');
  }

  return payload;
};

const resolveVideoUrl = (urlValue) => {
  const value = String(urlValue || '').trim();
  if (!value) return '';
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  return `${API_BASE}${value}`;
};

const formatWhen = (isoValue) => {
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return 'Recently';
  return date.toLocaleDateString();
};

const navLinkClasses = ({ isActive }) => (
  `rounded-md px-3 py-2 text-sm font-medium transition ${isActive ? 'bg-purple-600 text-white' : 'text-slate-700 hover:bg-indigo-100/90 hover:text-slate-900'}`
);

export default function Gallery() {
  const { user } = useContext(AuthContext);
  const permissionLevel = Number(user?.permission ?? user?.Permission ?? 0);
  const accessLevel = String(user?.access ?? user?.Access ?? '').trim().toLowerCase();
  const isFullAccessUser = accessLevel === 'admin' && permissionLevel === 1;
  const isPermissionRestricted = permissionLevel === 2 && !isFullAccessUser;

  const companyNameForMenu = String(user?.companyName ?? user?.CompanyName ?? '').trim();
  const showCompanyNameInMenu = Boolean(user?.insightAccount) && companyNameForMenu.length > 0;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadGallery = async () => {
    setLoading(true);
    setError('');

    const endpoints = [`${API_BASE}/gallery/add`, `${API_BASE}/get/gallery/add`];

    try {
      let payload = null;
      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          const response = await fetch(endpoint, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          });
          payload = await parseGalleryResponse(response);
          if (payload) break;
        } catch (err) {
          lastError = err;
        }
      }

      if (!payload) {
        throw new Error(lastError?.message || 'Failed to load gallery.');
      }

      const rows = Array.isArray(payload?.data) ? payload.data : [];
      setItems(rows);
    } catch (err) {
      setItems([]);
      setError(err?.message || 'Failed to load gallery.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGallery();
  }, []);

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const aTime = new Date(a?.CreatedAt || a?.createdAt || 0).getTime();
      const bTime = new Date(b?.CreatedAt || b?.createdAt || 0).getTime();
      return bTime - aTime;
    });
  }, [items]);

  const topRow = sortedItems.slice(0, 12);
  const archiveRow = sortedItems.slice(12);

  if (isPermissionRestricted) {
    return (
      <div
        className="min-h-screen bg-cover bg-center bg-no-repeat px-3 pb-8 pt-3 text-slate-900 sm:px-6"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(219,234,254,0.9) 0%, rgba(233,213,255,0.82) 45%, rgba(255,255,255,0.92) 100%), url('/avenorlogobackground.png')",
        }}
      >
        <div className="sticky top-0 z-40 rounded-xl border border-white/70 bg-white/70 shadow-[0_12px_35px_rgba(79,70,229,0.16)] backdrop-blur">
          <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-3 py-3 sm:px-6">
            <div className="flex items-center gap-3">
              <img src="/avenor.png" alt="InsightOS Avenor Logo" className="h-8 w-8" />
              <h1 className="text-base font-bold text-slate-900">InsightOS Gallery</h1>
              {showCompanyNameInMenu && (
                <span className="rounded-md border border-indigo-200/80 bg-indigo-100/80 px-2 py-0.5 text-xs font-semibold text-indigo-900">
                  {companyNameForMenu}
                </span>
              )}
            </div>
            <NavLink
              to="/logout"
              className="rounded-md bg-red-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-600"
            >
              Logout
            </NavLink>
          </div>
        </div>

        <div className="mx-auto mt-8 w-full max-w-4xl rounded-2xl border border-amber-500/40 bg-amber-600/10 px-6 py-8">
          <h2 className="text-lg font-semibold text-amber-300">Gallery Access Restricted</h2>
          <p className="mt-2 text-sm text-amber-100/90">
            Your account permission level does not allow access to AI and Ad Studio related features.
          </p>
        </div>
      </div>
    );
  }

  const renderRow = (rowItems, title) => (
    <section className="mt-8">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900 sm:text-xl">{title}</h2>
        <span className="text-xs text-slate-500">{rowItems.length} videos</span>
      </div>

      {rowItems.length === 0 ? (
        <div className="rounded-xl border border-indigo-100/80 bg-white/75 p-6 text-sm text-slate-500 shadow-sm">
          No videos here yet.
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-2 [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-indigo-300 [&::-webkit-scrollbar]:h-2">
          {rowItems.map((item, index) => {
            const src = resolveVideoUrl(item?.GalleryURL || item?.url);
            const titleText = String(item?.Title || item?.title || `Advert ${index + 1}`);
            const ownerText = String(item?.UserName || 'Unknown user');
            const creditsText = item?.Credits === null || item?.Credits === undefined || item?.Credits === ''
              ? 'N/A'
              : String(item.Credits);
            const when = formatWhen(item?.CreatedAt || item?.createdAt);
            return (
              <article
                key={`${item?.id || item?.GalleryURL || item?.url || 'gallery'}-${index}`}
                className="group w-[78vw] max-w-[320px] min-w-[260px] overflow-hidden rounded-xl border border-white/80 bg-white/85 shadow-[0_14px_38px_rgba(79,70,229,0.18)] transition hover:scale-[1.02] hover:border-indigo-400/70 sm:w-[32vw]"
              >
                <div className="relative aspect-video bg-black">
                  {src ? (
                    <video
                      src={src}
                      controls
                      preload="metadata"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-slate-500">Video unavailable</div>
                  )}
                </div>
                <div className="space-y-1 p-3">
                  <h3 className="line-clamp-2 text-sm font-semibold text-slate-900">{titleText}</h3>
                  <p className="text-xs text-slate-600">User: {ownerText}</p>
                  <p className="text-xs text-slate-600">Credits: {creditsText}</p>
                  <p className="text-xs text-slate-500">Created: {when}</p>
                  {src && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const response = await fetch(src, { credentials: 'include' });
                          const blob = await response.blob();
                          const blobUrl = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = blobUrl;
                          link.download = `${titleText.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.mp4`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          URL.revokeObjectURL(blobUrl);
                        } catch {
                          window.open(src, '_blank');
                        }
                      }}
                      className="mt-2 inline-flex w-full items-center justify-center rounded-md border border-indigo-200/80 bg-indigo-50/80 px-3 py-1.5 text-xs font-semibold text-indigo-800 hover:bg-indigo-100 hover:text-indigo-900 transition"
                    >
                      ↓ Download
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );

  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat px-3 pb-8 pt-3 sm:px-6"
      style={{
        backgroundImage:
          "linear-gradient(135deg, rgba(219,234,254,0.9) 0%, rgba(233,213,255,0.82) 45%, rgba(255,255,255,0.92) 100%), url('/avenorlogobackground.png')",
      }}
    >
      <div className="sticky top-0 z-40 rounded-xl border border-white/70 bg-white/70 shadow-[0_12px_35px_rgba(79,70,229,0.16)] backdrop-blur">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-3 px-3 py-3 sm:px-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <img src="/avenor.png" alt="InsightOS Avenor Logo" className="h-8 w-8" />
            <h1 className="text-base font-bold text-slate-900">InsightOS Gallery</h1>
            {showCompanyNameInMenu && (
              <span className="rounded-md border border-indigo-200/80 bg-indigo-100/80 px-2 py-0.5 text-xs font-semibold text-indigo-900">
                {companyNameForMenu}
              </span>
            )}
            <nav className="hidden items-center gap-2 md:flex">
              <NavLink to="/leads" end className={navLinkClasses}>Leads</NavLink>
              <NavLink to="/adverts" className={navLinkClasses}>Ad Studio</NavLink>
              <NavLink to="/gallery" className={navLinkClasses}>Gallery</NavLink>
              <NavLink to="/billing" className={navLinkClasses}>Billing</NavLink>
            </nav>
          </div>

          <div className="flex w-full items-center justify-start gap-2 md:w-auto md:justify-end">
            <button
              type="button"
              onClick={loadGallery}
              disabled={loading}
              className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-900 hover:bg-indigo-100 disabled:opacity-60"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <NavLink
              to="/logout"
              className="rounded-md bg-red-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-600"
            >
              Logout
            </NavLink>
          </div>
        </div>

        <div className="border-t border-white/70 px-3 py-2 md:hidden">
          <nav className="flex items-center gap-2">
            <NavLink to="/leads" end className={navLinkClasses}>Leads</NavLink>
            <NavLink to="/adverts" className={navLinkClasses}>Ad Studio</NavLink>
            <NavLink to="/gallery" className={navLinkClasses}>Gallery</NavLink>
            <NavLink to="/billing" className={navLinkClasses}>Billing</NavLink>
          </nav>
        </div>
      </div>

      <div className="mx-auto mt-6 w-full max-w-7xl">
        <section className="overflow-hidden rounded-2xl border border-white/80 bg-white/80 p-5 shadow-[0_24px_65px_rgba(79,70,229,0.2)] sm:p-7">
          <p className="inline-flex items-center rounded-full border border-indigo-300/70 bg-indigo-100/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-indigo-800">
            Your Gallery
          </p>
          <h2 className="mt-3 text-2xl font-black tracking-tight text-slate-900 sm:text-4xl">Your Generated Adverts</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-700 sm:text-base">
            Browse your latest videos in a streaming-style wall. New finished videos from Ad Studio are automatically saved to this gallery.
          </p>
        </section>

        {error && (
          <div className="mt-6 rounded-xl border border-red-700/60 bg-red-900/30 px-4 py-3 text-sm text-red-100">
            {error}
          </div>
        )}

        {loading ? (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={`skeleton-${index}`} className="overflow-hidden rounded-xl border border-white/80 bg-white/80 shadow-sm">
                <div className="aspect-video animate-pulse bg-indigo-100" />
                <div className="space-y-2 p-3">
                  <div className="h-3 w-2/3 animate-pulse rounded bg-indigo-100" />
                  <div className="h-2.5 w-1/3 animate-pulse rounded bg-indigo-100" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {renderRow(topRow, 'Continue Watching')}
            {renderRow(archiveRow, 'Library Archive')}
          </>
        )}
      </div>
    </div>
  );
}
